export const roundMoney = value => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
export const localizedNumber = value => Number(String(value ?? '').replace(',', '.'));
export const applyPricingStage = (rows, stageMode, itemValues, generalValue, grossValue, vatRate, increase) => {
  const currentNet = roundMoney(rows.reduce((sum, row) => sum + row.priceValue, 0));
  let automaticPercentage = null;
  let targetNet = null;
  let targetVat = null;
  const requestedGross = localizedNumber(grossValue);
  if (stageMode === 'target_gross' && requestedGross > 0 && currentNet > 0) {
    const gross = roundMoney(requestedGross);
    targetVat = roundMoney(gross * vatRate / (100 + vatRate));
    targetNet = roundMoney(gross - targetVat);
    automaticPercentage = increase
      ? (targetNet / currentNet - 1) * 100
      : (1 - targetNet / currentNet) * 100;
  }

  const adjusted = rows.map(row => {
    const stageBaseUnit = row.priceUnit;
    const stageBaseValue = row.priceValue;
    let percentage = localizedNumber(itemValues[row.number]) || 0;
    if (stageMode === 'general_markup' || stageMode === 'general_discount') {
      percentage = localizedNumber(generalValue) || 0;
    }
    if (stageMode === 'target_gross') percentage = automaticPercentage ?? 0;
    if (!percentage) return { ...row, adjustment: percentage, stageBaseUnit, stageBaseValue };
    const factor = 1 + (increase ? 1 : -1) * percentage / 100;
    const priceUnit = roundMoney(row.priceUnit * factor);
    return {
      ...row,
      adjustment: percentage,
      stageBaseUnit,
      stageBaseValue,
      priceUnit,
      priceValue: roundMoney(Number(row.quantity) * priceUnit),
    };
  });

  if (targetNet !== null && adjusted.length) {
    const calculatedNet = roundMoney(adjusted.reduce((sum, row) => sum + row.priceValue, 0));
    const residual = roundMoney(targetNet - calculatedNet);
    const residualCents = Math.round(residual * 100);
    const anchor = adjusted.find(row => Number(row.quantity) === 1)
      || adjusted.find(row => Number(row.quantity) > 0 && residualCents % Number(row.quantity) === 0);
    if (residual && anchor) {
      anchor.priceUnit = roundMoney(anchor.priceUnit + residual / Number(anchor.quantity));
      anchor.priceValue = roundMoney(anchor.priceUnit * Number(anchor.quantity));
      anchor.adjustment = increase
        ? (anchor.priceUnit / Number(anchor.stageBaseUnit) - 1) * 100
        : (1 - anchor.priceUnit / Number(anchor.stageBaseUnit)) * 100;
    }
  }

  const net = roundMoney(adjusted.reduce((sum, row) => sum + row.priceValue, 0));
  const vat = targetNet !== null && targetVat !== null
    ? roundMoney(requestedGross - net)
    : roundMoney(net * vatRate / 100);
  return {
    rows: adjusted,
    net,
    vat,
    gross: roundMoney(net + vat),
    automaticPercentage,
  };
};
export const exchangeMultiplier = (value, direction) => {
  const rate = localizedNumber(value);
  if (!(rate > 0)) return 0;
  return direction === 'target_to_source' ? 1 / rate : rate;
};
export const money = (value, currency = 'PLN') => new Intl.NumberFormat('pl-PL', {
  style: 'currency', currency: currency === 'EURO' ? 'EUR' : currency,
}).format(Number(value || 0));
export const polishNoun = (count, one, few, many) => {
  if (count === 1) return one;
  const lastTwo = count % 100;
  const last = count % 10;
  return last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14) ? few : many;
};

export const buildMarkupPreview = ({
  analysis, workflowMode, markupMode, markups, generalMarkup, markupTargetGross,
  vat, targetCurrency, exchangeRate, exchangeRateDirection,
}) => {
  if (!analysis) return { rows: [], net: 0, vat: 0, gross: 0, automaticPercentage: null };
  const conversionRate = targetCurrency
    ? exchangeMultiplier(exchangeRate, exchangeRateDirection)
    : 1;
  const previewCurrency = targetCurrency || analysis.currency;
  const rows = analysis.items.map(item => {
    const converted = Boolean(targetCurrency);
    const priceUnit = converted
      ? roundMoney(Number(item.unit_net) * conversionRate)
      : Number(item.unit_net);
    return {
      ...item,
      originalCurrency: item.currency,
      currency: previewCurrency,
      priceUnit,
      priceValue: converted
        ? roundMoney(Number(item.quantity) * priceUnit)
        : Number(item.value_net),
    };
  });
  if (workflowMode !== 'markup_and_discount') {
    const net = roundMoney(rows.reduce((sum, row) => sum + row.priceValue, 0));
    const vatAmount = roundMoney(net * Number(vat || 0) / 100);
    return { rows, net, vat: vatAmount, gross: roundMoney(net + vatAmount), automaticPercentage: null };
  }
  return applyPricingStage(
    rows, markupMode, markups, generalMarkup, markupTargetGross,
    Number(vat || 0), true,
  );
};

export const buildDiscountPreview = ({
  analysis, workflowMode, markupMode, markupPreview, mode, discounts,
  generalDiscount, targetGross, vat,
}) => {
  if (!analysis) return { rows: [], net: 0, vat: 0, gross: 0, automaticDiscount: null };
  const result = applyPricingStage(
    markupPreview.rows, mode, discounts, generalDiscount, targetGross,
    Number(vat || 0), false,
  );
  const discountDidNotChangePrices = result.rows.every((row, index) => (
    row.priceUnit === markupPreview.rows[index]?.priceUnit
    && row.priceValue === markupPreview.rows[index]?.priceValue
  ));
  if (
    workflowMode === 'markup_and_discount'
    && markupMode === 'target_gross'
    && mode !== 'target_gross'
    && discountDidNotChangePrices
  ) {
    result.vat = markupPreview.vat;
    result.gross = markupPreview.gross;
  }
  return {
    ...result,
    automaticDiscount: result.automaticPercentage,
    rows: result.rows.map(row => ({
      ...row,
      discount: row.adjustment,
      newUnit: row.priceUnit,
      newValue: row.priceValue,
    })),
  };
};

export const convertOriginalValues = ({
  analysis, targetCurrency, exchangeRate, exchangeRateDirection,
}) => {
  if (!analysis || !targetCurrency) return null;
  const conversionRate = exchangeMultiplier(exchangeRate, exchangeRateDirection);
  if (!(conversionRate > 0)) return null;
  const net = roundMoney(analysis.items.reduce((sum, item) => {
    const convertedUnit = roundMoney(Number(item.unit_net) * conversionRate);
    return sum + roundMoney(Number(item.quantity) * convertedUnit);
  }, 0));
  const vatAmount = roundMoney(net * Number(analysis.vat_rate) / 100);
  return {
    net,
    vat: vatAmount,
    gross: roundMoney(net + vatAmount),
  };
};

export const validatePricingSteps = ({
  analysis, convertedOriginalValues, markupPreview, vat, changeCurrency, targetCurrency,
  exchangeRate, exchangeRateDirection, markupMode, markups, generalMarkup,
  markupTargetGross, mode, discounts, generalDiscount, targetGross,
}) => {
  const vatNumber = localizedNumber(vat);
  const currencyConfigurationInvalid = changeCurrency
    && (!targetCurrency || !(exchangeMultiplier(exchangeRate, exchangeRateDirection) > 0));
  const sourcePricingNet = convertedOriginalValues?.net ?? Number(analysis?.total_net || 0);
  const sourcePricingGross = roundMoney(
    sourcePricingNet + roundMoney(sourcePricingNet * (Number.isFinite(vatNumber) ? vatNumber : 0) / 100),
  );
  const markupTargetNumber = localizedNumber(markupTargetGross);
  const markupValuesInvalid = markupMode === 'markups' && Object.values(markups).some(value => (
    String(value).trim() === '' || !Number.isFinite(localizedNumber(value)) || localizedNumber(value) < 0
  ));
  const markupStepInvalid = (
    !Number.isFinite(vatNumber) || vatNumber < 0 || vatNumber > 23
    || markupValuesInvalid
    || (markupMode === 'general_markup' && (
      String(generalMarkup).trim() === ''
      || !Number.isFinite(localizedNumber(generalMarkup))
      || localizedNumber(generalMarkup) < 0
    ))
    || (markupMode === 'target_gross' && (
      !Number.isFinite(markupTargetNumber)
      || markupTargetNumber <= 0
      || markupTargetNumber < sourcePricingGross
    ))
    || currencyConfigurationInvalid
  );
  const discountValuesInvalid = mode === 'discounts' && Object.values(discounts).some(value => (
    String(value).trim() === '' || !Number.isFinite(localizedNumber(value))
    || localizedNumber(value) < 0 || localizedNumber(value) > 100
  ));
  const discountStepInvalid = (
    !Number.isFinite(vatNumber) || vatNumber < 0 || vatNumber > 23
    || discountValuesInvalid
    || (mode === 'general_discount' && (
      String(generalDiscount).trim() === ''
      || !Number.isFinite(localizedNumber(generalDiscount))
      || localizedNumber(generalDiscount) < 0
      || localizedNumber(generalDiscount) > 100
    ))
    || (mode === 'target_gross' && (
      !Number.isFinite(localizedNumber(targetGross))
      || localizedNumber(targetGross) <= 0
      || localizedNumber(targetGross) > markupPreview.gross
    ))
    || currencyConfigurationInvalid
  );
  return {
    vatNumber, currencyConfigurationInvalid, sourcePricingGross, markupTargetNumber,
    markupStepInvalid, discountStepInvalid,
  };
};
